import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';
import { z } from 'zod/v4';
import { createValidatedStorage, useValidatedStorage } from '~/global-state/validated-storage';
import { useUrlParams } from '~/hooks/useUrlParams';
import type { DateRangeString } from '~/utils/temporal';

const dateRangeStringSchema = z.tuple([z.string().nullable(), z.string().nullable()]);

export const globalFiltersSchema = z.object({
  period: dateRangeStringSchema,
});

export interface GlobalFilters {
  period: DateRangeString;
}

type FilterKey = keyof GlobalFilters;

export const initialGlobalFilters: GlobalFilters = {
  period: [null, null],
};

/* -------------------------------------------------------------------------------------------------
 * Session storage adapter
 *
 * The shared period is persisted to sessionStorage so it survives navigation and refresh within a
 * tab/session, but resets when the browser is closed (each new session recomputes a fresh default).
 * -----------------------------------------------------------------------------------------------*/

export const globalFiltersSessionStorage = createValidatedStorage('session', globalFiltersSchema);

const STORAGE_KEY_BASE = 'global_period';

export function getGlobalPeriodStorageKey(orgId: string | undefined) {
  return `${orgId}.${STORAGE_KEY_BASE}`;
}

function useGlobalFiltersStorage() {
  const { user } = useAuth0();
  return useValidatedStorage<GlobalFilters>(
    globalFiltersSessionStorage,
    getGlobalPeriodStorageKey(user?.org_id),
    initialGlobalFilters,
  );
}

/* -------------------------------------------------------------------------------------------------
 * Hooks
 * -----------------------------------------------------------------------------------------------*/

export function useGetGlobalFilterValue(key: FilterKey) {
  const [filters] = useGlobalFiltersStorage();
  return useMemo(() => filters[key], [filters, key]);
}

export function useSetGlobalPeriodFilter() {
  const [, setFilters] = useGlobalFiltersStorage();
  const { setUrlParams } = useUrlParams();

  return useCallback(
    (period: DateRangeString, replaceURL = false) => {
      const [startDate, endDate] = period;

      if (!startDate || !endDate) {
        return;
      }

      setFilters((prev) => ({ ...prev, period }));

      if (replaceURL) {
        const locationHash = new URLSearchParams(location.hash.slice(1));
        const params = new URLSearchParams(locationHash);

        params.set('start_datetime', startDate);
        params.set('end_datetime', endDate);
        setUrlParams(undefined, params, replaceURL);
      }
    },
    [setFilters, setUrlParams],
  );
}
